import React from 'react';
import { Text } from '@mantine/core';
import { useTheme } from '../../../context/ThemeProvider.tsx';
import {
  type RateGroup,
  type DragRect,
} from '../../../hooks/parking-services/useParkingRateGroups.ts';
import { DAYS, fmt } from '../../../utils/ParkingRateTime.ts';

interface ParkingWeekGridProps {
  numDays: number;
  groups: RateGroup<unknown>[];
  editingGroupId: number | null;
  dragRect: DragRect | null;
  columnLabels?: string[];
  onCellDown: (day: number, slot: number) => void;
  onCellEnter: (day: number, slot: number) => void;
}

const NUM_SLOTS = 48;
const CELL_HEIGHT = 12;

const ParkingWeekGrid = React.memo<ParkingWeekGridProps>(
  ({
    numDays,
    groups,
    editingGroupId,
    dragRect,
    columnLabels,
    onCellDown,
    onCellEnter,
  }) => {
    const isDateMode = !!columnLabels;
    const { colorScheme } = useTheme();

    // 判斷某天某 slot 是否被某個 group 覆蓋
    const findGroup = (d: number, slot: number) => {
      for (const gr of groups) {
        const b = gr.blocks.find(
          (bl) => bl.days.includes(d) && slot >= bl.start && slot <= bl.end,
        );
        if (b) return gr;
      }
      return undefined;
    };

    return (
      <div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `48px repeat(${numDays}, 1fr)`,
            minWidth: isDateMode ? Math.max(160, 48 + numDays * 64) : 610,
            userSelect: 'none',
          }}
        >
          {/* 欄標題 */}
          <div
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 2,
              background:
                colorScheme === 'light' ? 'white' : 'var(--mantine-color-body)',
            }}
          />
          {Array.from({ length: numDays }, (_, d) => (
            <div
              key={d}
              style={{
                padding: '8px 4px 10px',
                textAlign: 'center',
                borderBottom: '2px solid #e5e7eb',
                position: 'sticky',
                top: 0,
                zIndex: 1,
                background:
                  colorScheme === 'light'
                    ? 'white'
                    : 'var(--mantine-color-body)',
              }}
            >
              {columnLabels ? (
                columnLabels[d].split('\n').map((line, i) => (
                  <div
                    key={i}
                    style={{
                      fontSize: i === 0 ? 13 : 11,
                      fontWeight: i === 0 ? 600 : 400,
                      color: i === 0 ? undefined : '#9ca3af',
                      lineHeight: 1.4,
                    }}
                  >
                    {line}
                  </div>
                ))
              ) : (
                <div style={{ fontSize: 13, fontWeight: 600 }}>{DAYS[d]}</div>
              )}
            </div>
          ))}

          {/* 時段列 */}
          {Array.from({ length: NUM_SLOTS }, (_, slot) => (
            <React.Fragment key={slot}>
              {/* 時間標籤欄 */}
              <div
                style={{
                  fontSize: 10,
                  color: '#9ca3af',
                  paddingRight: 8,
                  height: CELL_HEIGHT,
                  position: 'relative',
                  top: slot % 2 === 0 ? -6 : 0,
                  alignContent: 'center',
                }}
              >
                <Text style={{ fontSize: 10, lineHeight: '12px' }}>
                  {slot !== 0 && slot % 2 === 0 ? fmt(slot) : ''}
                </Text>
              </div>

              {/* 各天格子 */}
              {Array.from({ length: numDays }, (_, d) => {
                const foundGroup = findGroup(d, slot);
                const inRect =
                  !!dragRect &&
                  slot >= dragRect.minSlot &&
                  slot <= dragRect.maxSlot &&
                  d >= dragRect.minDay &&
                  d <= dragRect.maxDay;
                const inDragAdd =
                  !foundGroup && inRect && dragRect?.mode === 'add';
                const inDragRemove =
                  !!foundGroup && inRect && dragRect?.mode === 'remove';
                const isEditingCell =
                  !!foundGroup && foundGroup.id === editingGroupId;

                const bg = inDragRemove
                  ? '#fca5a5'
                  : foundGroup
                    ? foundGroup.color + '33'
                    : inDragAdd
                      ? '#bfdbfe'
                      : undefined;

                return (
                  <div
                    key={d}
                    data-day={d}
                    data-slot={slot}
                    style={{
                      height: CELL_HEIGHT,
                      borderRight:
                        d < numDays - 1 ? '1px solid #f3f4f6' : undefined,
                      borderBottom:
                        slot % 2 === 1
                          ? foundGroup
                            ? colorScheme === 'light'
                              ? '1px solid #f9fafb'
                              : '1px solid rgba(222,226,230,0.6)'
                            : colorScheme === 'light'
                              ? '1px solid #e5e7eb'
                              : '1px solid rgba(222,226,230,0.6)'
                          : foundGroup
                            ? colorScheme === 'light'
                              ? '1px solid #e5e7eb'
                              : '1px solid rgba(249,250,251,0.2)'
                            : colorScheme === 'light'
                              ? '1px solid #f9fafb'
                              : '1px solid rgba(249,250,251,0.2)',
                      outline: isEditingCell
                        ? `1px solid ${foundGroup!.color}80`
                        : undefined,
                      outlineOffset: '-1px',
                      cursor: foundGroup ? 'cell' : 'crosshair',
                      background: bg,
                      transition: 'background 0.04s',
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onCellDown(d, slot);
                    }}
                    onMouseEnter={() => onCellEnter(d, slot)}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  },
);
ParkingWeekGrid.displayName = 'ParkingWeekGrid';

export default ParkingWeekGrid;
